import React, { useState, useEffect } from 'react';
import './QuickPatientSignup.css';
import { useTranslation } from 'react-i18next';
import FormatUrl from '../../utils/UrlFormatter';
import { getClinicId } from '../../utils/config';
import { toast } from 'react-toastify';

const QuickPatientSignup = ({ user }) => {
  const { t } = useTranslation();
  const clinicId = getClinicId(user);

  const [formDataState, setFormDataState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    setErrors({});
  }, []);

  const formatCanadianPhone = (numericValue) => {
    if (!numericValue) return '';
    if (numericValue.length <= 3) {
      return `(${numericValue}`;
    } else if (numericValue.length <= 6) {
      return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
    } else {
      return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const rawValue = e.target.value;
    const numericValue = rawValue.replace(/\D/g, '').slice(0, 10);

    setFormDataState((prev) => ({ ...prev, phone: numericValue }));
    setErrors((prev) => ({ ...prev, phone: undefined }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormDataState((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateFields = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    if (!formDataState.firstName) {
      newErrors.firstName = t("register_user.first_name_required");
    }

    if (!formDataState.lastName) {
      newErrors.lastName = t("register_user.last_name_required");
    }

    if (!emailRegex.test(formDataState.email)) {
      newErrors.email = t("register_user.valid_email_required");
    }

    if (!phoneRegex.test(formDataState.phone)) {
      newErrors.phone = t("register_user.valid_phone_required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    try {
      const csrfRes = await fetch(FormatUrl('/csrf-token/'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!csrfRes.ok) throw new Error('Failed to fetch CSRF token');
      const { csrfToken } = await csrfRes.json();

      const formData = new URLSearchParams();
      formData.append('firstName', formDataState.firstName);
      formData.append('lastName', formDataState.lastName);
      formData.append('email', formDataState.email);
      formData.append('phone', `+1${formDataState.phone}`);
      formData.append('clinic_id', String(clinicId));

      const response = await fetch(FormatUrl('/owner-add-patient/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRFToken': csrfToken,
        },
        body: formData,
        credentials: 'include',
      });

      const result = await response.json();

      if (response.ok && result.status === 'Success') {
        toast.success(t('register_main.client_signup_success'));
        setTimeout(() => {
          setFormDataState({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
          });
          setErrors({});
        }, 2000); 
      } else {
        toast.error(result.errorMsg || 'Signup failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred during submission');
    }
  };

  if (!['CO', 'CA'].includes(user?.role)) return null;

  return (
    <section className="quick_signup_container_quick">
      <h2 className="quick_signup_title_quick">{t('register_main.quick_signup')}</h2>
      <form onSubmit={handleSubmit} className="quick_signup_form_quick">
        <div className="form-row_quick">
          <label htmlFor="firstName" className="form-label_quick">
            {t('register_main.first_name')} <span className="required_quick">*</span>
          </label>
          <input
            type="text"
            name="firstName"
            className="form-input_quick"
            value={formDataState.firstName}
            onChange={handleChange}
          />
          {errors.firstName && <p className="error-text_quick">{errors.firstName}</p>}
        </div>

        <div className="form-row_quick">
          <label htmlFor="lastName" className="form-label_quick">
            {t('register_main.last_name')} <span className="required_quick">*</span>
          </label>
          <input
            type="text"
            name="lastName"
            className="form-input_quick"
            value={formDataState.lastName}
            onChange={handleChange}
          />
          {errors.lastName && <p className="error-text_quick">{errors.lastName}</p>}
        </div>

        <div className="form-row_quick">
          <label htmlFor="email" className="form-label_quick">
            {t('register_main.email')} <span className="required_quick">*</span>
          </label>
          <input
            type="email"
            name="email"
            className="form-input_quick"
            value={formDataState.email}
            onChange={handleChange}
          />
          {errors.email && <p className="error-text_quick">{errors.email}</p>}
        </div>

        <div className="form-row_quick">
          <label htmlFor="phone" className="form-label_quick">
            {t('register_main.phone')} <span className="required_quick">*</span>
          </label>
          <div className="phone-input-container_quick">
            <input type="text" className="phone-prefix_quick" value="+1" disabled />
            <input
              type="text"
              name="phone"
              className="form-input_quick phone-main-input_quick"
              value={formatCanadianPhone(formDataState.phone)}
              onChange={handlePhoneChange}
              placeholder="(XXX) XXX-XXXX"
              maxLength="14"
            />
          </div>
          {errors.phone && <p className="error-text_quick">{errors.phone}</p>}
        </div>

        <button type="submit" className="quick_signup_button_quick">
          {t('register_main.submit')}
        </button>
      </form>
    </section>
  );
};

export default QuickPatientSignup;
